import { c as _c } from "react/compiler-runtime";
import { createLogger } from '@svs/nova-core/log';
import { useQueryClient } from '@tanstack/react-query';
import { settingsQueryKeys } from '../../../../lib/entry.js';
import { useMutation } from '../../../../tanstack/entry.js';
import { updateCouponSettings } from '../../../actions/coupon/updateCouponSettings.js';
import { useEngine } from '../../engine/entry.js';
import { useCouponSettings } from './useCouponSettings.js';
const logger = createLogger('poolbetting.couponsettings-hook');
/**
 * Updates current user coupon settings. Hook returns an object containing an loading indicator and an update function used to mutate the settings.
 * If successful the mutation will update the corresponding GET query function; {@link useCouponSettings}.
 *
 * Read more on:
 * - {@link https://tkdodo.eu/blog/mastering-mutations-in-react-query mutations}.
 * - {@link https://tanstack.com/query/latest/docs/framework/react/guides/optimistic-updates?from=reactQueryV3#updating-a-list-of-todos-when-adding-a-new-todo optimistic updates}.
 * - {@link https://tkdodo.eu/blog/automatic-query-invalidation-after-mutations query invalidation}.
 *
 * @param product Product to fetch settings for.
 * @param settingsKey Settings key, to get settings for either play or my bets/results.
 * @returns Current product coupon settings.
 */
export function useUpdateCouponSettings(product, settingsKey) {
  const $ = _c(26);
  const definition = useEngine();
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== definition || $[1] !== product || $[2] !== queryClient || $[3] !== settingsKey) {
    t0 = nextFormDataSettings => {
      const queryKeys = settingsQueryKeys.product(product, settingsKey);
      const previousSettings = queryClient.getQueryData(queryKeys);
      const nextSettings = definition.calculateCouponSettings({
        formData: nextFormDataSettings,
        currentCouponSettings: previousSettings,
        definition,
        settingsKey
      });
      queryClient.setQueryData(queryKeys, () => nextSettings);
      return nextSettings;
    };
    $[0] = definition;
    $[1] = product;
    $[2] = queryClient;
    $[3] = settingsKey;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  const optimisticUpdate = t0;
  let t1;
  if ($[5] !== optimisticUpdate) {
    t1 = {
      onDebounce: optimisticUpdate
    };
    $[5] = optimisticUpdate;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== product || $[8] !== queryClient || $[9] !== settingsKey) {
    t2 = async settings => {
      const queryKeys_0 = settingsQueryKeys.product(product, settingsKey);
      const previousSettings_0 = queryClient.getQueryData(queryKeys_0);
      const data = previousSettings_0 ? {
        status: "SUCCESS",
        data: previousSettings_0
      } : {
        status: "IDLE"
      };
      const response = await updateCouponSettings({
        product,
        previousState: data,
        formData: settings,
        settingsKey
      });
      if (response.status === "SUCCESS") {
        return response.data;
      }
      if (response.status === "ERROR") {
        throw new Error(response.error);
      }
      throw new Error("Unknown state");
    };
    $[7] = product;
    $[8] = queryClient;
    $[9] = settingsKey;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== definition || $[12] !== product || $[13] !== queryClient || $[14] !== settingsKey) {
    t3 = async nextFormDataSettings_0 => {
      const queryKeys_1 = settingsQueryKeys.product(product, settingsKey);
      await queryClient.cancelQueries({
        queryKey: queryKeys_1
      });
      const previousSettings_1 = queryClient.getQueryData(queryKeys_1);
      const nextSettings_0 = definition.calculateCouponSettings({
        formData: nextFormDataSettings_0,
        currentCouponSettings: previousSettings_1,
        definition,
        settingsKey
      });
      queryClient.setQueryData(queryKeys_1, () => nextSettings_0);
      return {
        previousSettings: previousSettings_1
      };
    };
    $[11] = definition;
    $[12] = product;
    $[13] = queryClient;
    $[14] = settingsKey;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  let t4;
  let t5;
  if ($[16] !== product || $[17] !== settingsKey) {
    t4 = (error, nextFormDataSettings_1, context) => {
      logger.debug("Error while updating coupon settings", {
        error,
        product,
        settingsKey,
        previousSettings: context?.previousSettings
      });
    };
    t5 = () => {
      logger.debug("Successfully persisted coupon settings", {
        product,
        settingsKey
      });
    };
    $[16] = product;
    $[17] = settingsKey;
    $[18] = t4;
    $[19] = t5;
  } else {
    t4 = $[18];
    t5 = $[19];
  }
  let t6;
  if ($[20] !== t1 || $[21] !== t2 || $[22] !== t3 || $[23] !== t4 || $[24] !== t5) {
    t6 = {
      custom: t1,
      mutationFn: t2,
      onMutate: t3,
      onError: t4,
      onSuccess: t5
    };
    $[20] = t1;
    $[21] = t2;
    $[22] = t3;
    $[23] = t4;
    $[24] = t5;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  return useMutation(t6);
}